import { TaxonomyItem } from "library/types";
import { ProcessedTaxonomyState } from "library/types/types";
import { useMemo } from "react";
import { useGetTaxonomies } from "state/queries/useGetTaxonomies";

/**
 * Hook that transforms raw taxonomy API data into a structured format with byId, byCode, and items
 * for easy lookup and iteration.
 *
 * Returns null if taxonomy data is not yet loaded, or an empty object if there's no data.
 *
 * @returns Processed taxonomy state with all groups transformed, or null if not loaded
 */
export const useTaxonomy = (): ProcessedTaxonomyState | null => {
  // Don't fetch - just use cached data from App.tsx
  const { data: rawTaxonomy, isLoading } = useGetTaxonomies(false);

  const processedTaxonomy = useMemo(() => {
    // Return null if still loading or no data yet
    if (isLoading || !rawTaxonomy) return null;

    const result: ProcessedTaxonomyState = {};

    // Transform each taxonomy group (e.g., "OfferStatus", "VehicleType")
    Object.entries(rawTaxonomy).forEach(([groupName, items]) => {
      // Create byId lookup: { 1: { id: 1, code: "...", ... }, 2: { ... } }
      const byId: { [id: number]: TaxonomyItem } = {};
      items.forEach((item) => {
        byId[item.id] = item;
      });

      // Create byCode lookup: { "OfferStatus.IN_PROGRESS": { id: 2, code: "...", ... } }
      const byCode: { [code: string]: TaxonomyItem } = {};
      items.forEach((item) => {
        byCode[item.code] = item;
      });

      result[groupName] = {
        byId,
        byCode,
        items,
      };
    });

    return result;
  }, [rawTaxonomy, isLoading]);

  return processedTaxonomy;
};
